import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Typography, Badge, Drawer, message } from 'antd';
import {
  DashboardOutlined,
  BankOutlined,
  TeamOutlined,
  BarChartOutlined,
  ExperimentOutlined,
  SettingOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  BellOutlined,
  BookOutlined,
  FormOutlined,
  EnvironmentOutlined,
  CustomerServiceOutlined,
  CloseOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useNauvatekSupportUnreadCount } from '../../hooks/useSupportTickets';
import './SuperAdminLayout.css';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

// Palette de couleurs Super Admin moderne (violet élégant)
const COLORS = {
  primary: '#1890ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  sidebarDark: '#1a0033', // Violet très foncé
  sidebarMid: '#2d1157', // Violet moyen
  sidebarLight: '#3d1a6b', // Violet plus clair
  light: '#f0f2f5',
  white: '#ffffff',
  gray: '#8c8c8c',
  border: '#d9d9d9',
  accent: '#8b5cf6', // Violet moderne plus lumineux
  accentHover: '#a78bfa', // Violet hover encore plus clair
};

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [refreshingApp, setRefreshingApp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, signOut } = useAuth();
  const { t } = useAppTranslation();
  const { data: supportUnreadCount = 0 } = useNauvatekSupportUnreadCount();

  const handleRefreshApp = () => {
    if (refreshingApp) return;
    setRefreshingApp(true);
    void queryClient
      .invalidateQueries()
      .then(() => {
        message.success(t('account.refreshAppDone'));
      })
      .finally(() => {
        setRefreshingApp(false);
      });
  };

  // Détection du mode mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileDrawerVisible(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Styles CSS personnalisés pour le menu Super Admin (violet moderne)
  const menuStyles = `
    .super-admin-menu {
      background: transparent !important;
      padding-bottom: 20px !important;
    }
    .super-admin-menu .ant-menu-item {
      color: rgba(255, 255, 255, 0.75) !important;
      border-radius: 10px !important;
      margin: 6px 12px !important;
      padding-left: 20px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      border: 1px solid transparent !important;
    }
    .super-admin-menu .ant-menu-item .ant-menu-item-icon {
      color: rgba(255, 255, 255, 0.75) !important;
      font-size: 18px !important;
    }
    .super-admin-menu .ant-menu-item-selected {
      background: linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentHover} 100%) !important;
      border-right: none !important;
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
    }
    .super-admin-menu .ant-menu-item-selected .ant-menu-item-icon,
    .super-admin-menu .ant-menu-item-selected .ant-menu-title-content {
      color: #ffffff !important;
      font-weight: 600 !important;
    }
    .super-admin-menu .ant-menu-item:hover {
      background: rgba(139, 92, 246, 0.15) !important;
      transform: translateX(4px) !important;
      border: 1px solid rgba(139, 92, 246, 0.3) !important;
    }
    .super-admin-menu .ant-menu-item:hover .ant-menu-item-icon,
    .super-admin-menu .ant-menu-item:hover .ant-menu-title-content {
      color: ${COLORS.accentHover} !important;
    }
    .super-admin-sider {
      background: linear-gradient(180deg, ${COLORS.sidebarDark} 0%, ${COLORS.sidebarMid} 50%, ${COLORS.sidebarLight} 100%) !important;
    }
  `;

  // Menu items pour SuperAdmin (simplifié)
  const menuItems = [
    {
      key: '/super-admin/businesses',
      icon: <BankOutlined />,
      label: 'Gestion des Businesses',
    },
    {
      key: '/super-admin/users',
      icon: <TeamOutlined />,
      label: 'Gestion des Utilisateurs',
    },
    {
      key: '/super-admin/adoption',
      icon: <BarChartOutlined />,
      label: 'Adoption & usage',
    },
    {
      key: '/super-admin/installation-requests',
      icon: <FormOutlined />,
      label: 'Demandes d\'installation',
    },
    {
      key: '/super-admin/pickup-cities',
      icon: <EnvironmentOutlined />,
      label: 'Villes & quartiers',
    },
    {
      key: '/super-admin/support-tickets',
      icon: <CustomerServiceOutlined />,
      label: (
        <span>
          Support clients
          {supportUnreadCount > 0 ? (
            <Badge
              count={supportUnreadCount}
              size="small"
              style={{ marginLeft: 8 }}
            />
          ) : null}
        </span>
      ),
    },
    {
      key: 'documentation',
      icon: <BookOutlined />,
      label: 'Documentation',
      onClick: () => window.open('/documentation', '_blank'),
    },
  ];

  // Mettre à jour les clés sélectionnées selon la route
  useEffect(() => {
    const currentPath = location.pathname;
    setSelectedKeys([currentPath]);
  }, [location.pathname]);

  const handleMenuClick = ({ key }: { key: string }) => {
    // Ne pas naviguer pour la documentation (a son propre onClick)
    if (key !== 'documentation') {
      navigate(key);
    }
    // Fermer le drawer mobile après la navigation
    if (isMobile) {
      setMobileDrawerVisible(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Paramètres',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Déconnexion',
      onClick: handleSignOut,
    },
  ];

  return (
    <>
      {/* Injection des styles CSS */}
      <style dangerouslySetInnerHTML={{ __html: menuStyles }} />
      
      <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar Desktop */}
      {!isMobile && (
        <Sider 
          trigger={null} 
          collapsible 
          collapsed={collapsed}
          width={260}
          collapsedWidth={80}
          className="super-admin-sider"
          style={{
            boxShadow: '2px 0 12px rgba(139, 92, 246, 0.2)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Overlay pour effet de profondeur */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(139, 92, 246, 0.15), transparent)',
            pointerEvents: 'none',
            zIndex: 0,
          }} />
          
          {/* Logo Super Admin */}
          <div 
            style={{
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              marginBottom: '20px',
              background: 'rgba(0, 0, 0, 0.2)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentHover} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
              }}>
                <Text style={{ 
                  color: '#fff', 
                  fontSize: '16px',
                  fontWeight: 'bold',
                }}>
                  SA
                </Text>
              </div>
              {!collapsed && (
                <Text 
                  style={{ 
                    color: '#fff', 
                    fontSize: '18px',
                    fontWeight: '700',
                    letterSpacing: '0.5px',
                    textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  SuperAdmin
                </Text>
              )}
            </div>
          </div>

          {/* Menu de navigation */}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedKeys}
            items={menuItems}
            onClick={handleMenuClick}
            className="super-admin-menu"
            style={{
              background: 'transparent',
              border: 'none',
              position: 'relative',
              zIndex: 1,
            }}
          />
        </Sider>
      )}

      {/* Drawer Mobile */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentHover} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
            }}>
              <Text style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold' }}>SA</Text>
            </div>
            <Text strong style={{ fontSize: '18px', color: COLORS.accent }}>
              SuperAdmin
            </Text>
          </div>
        }
        placement="left"
        onClose={() => setMobileDrawerVisible(false)}
        open={mobileDrawerVisible}
        width={280}
        styles={{
          body: { 
            padding: 0, 
            background: `linear-gradient(180deg, ${COLORS.sidebarDark} 0%, ${COLORS.sidebarMid} 50%, ${COLORS.sidebarLight} 100%)`,
            position: 'relative',
          }
        }}
        closeIcon={<CloseOutlined style={{ color: COLORS.accent }} />}
      >
        {/* Overlay pour effet de profondeur mobile */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at top right, rgba(139, 92, 246, 0.15), transparent)',
          pointerEvents: 'none',
          zIndex: 0,
        }} />
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          items={menuItems}
          onClick={handleMenuClick}
          className="super-admin-menu"
          style={{
            background: 'transparent',
            border: 'none',
            position: 'relative',
            zIndex: 1,
            paddingTop: '16px',
          }}
        />
      </Drawer>

      <Layout>
        {/* Header Super Admin */}
        <Header 
          style={{ 
            background: `linear-gradient(90deg, #fff 0%, rgba(139, 92, 246, 0.04) 100%)`, 
            padding: isMobile ? '0 16px' : '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.08)',
            zIndex: 1,
            borderBottom: `2px solid ${COLORS.accent}`,
          }}
        >
          {/* Bouton toggle sidebar */}
          <Button
            type="text"
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => isMobile ? setMobileDrawerVisible(true) : setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: isMobile ? 48 : 64,
              height: isMobile ? 48 : 64,
            }}
          />

          {/* Titre de la page actuelle */}
          {!isMobile && (
            <div style={{ flex: 1, marginLeft: '24px' }}>
              <Text strong style={{ fontSize: '18px', color: COLORS.accent }}>
                {menuItems.find(
                  (item) =>
                    typeof item.key === 'string' &&
                    (item.key === location.pathname ||
                      (item.key !== '/' && location.pathname.startsWith(String(item.key))))
                )?.label || 'SuperAdmin'}
              </Text>
            </div>
          )}

          {/* Actions du header */}
          <Space size={isMobile ? 'small' : 'middle'}>
            <Badge count={supportUnreadCount} size="small">
              <Button
                type={isMobile ? 'primary' : 'default'}
                shape={isMobile ? 'circle' : 'default'}
                icon={<CustomerServiceOutlined />}
                onClick={() => navigate('/super-admin/support-tickets')}
                style={
                  isMobile
                    ? {
                        width: 36,
                        height: 36,
                        minWidth: 36,
                        padding: 0,
                        background: COLORS.accent,
                        borderColor: COLORS.accent,
                        color: '#fff',
                      }
                    : {
                        color: COLORS.accent,
                        borderColor: COLORS.accent,
                        fontWeight: 600,
                      }
                }
                title="Support clients"
              >
                {!isMobile && 'Support'}
              </Button>
            </Badge>
            {/* Bouton Documentation - visible uniquement sur desktop */}
            {!isMobile && (
              <Button
                type="text"
                icon={<BookOutlined />}
                onClick={() => window.open('/documentation', '_blank')}
                style={{
                  color: COLORS.accent,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: 500,
                }}
                title="Documentation"
              >
                Documentation
              </Button>
            )}

            {/* Notifications */}
            <Badge count={0} size="small">
              <Button 
                type="text" 
                icon={<BellOutlined />} 
                style={{ fontSize: '16px', color: COLORS.gray }}
              />
            </Badge>

            <Button
              type={isMobile ? 'primary' : 'default'}
              shape={isMobile ? 'circle' : 'default'}
              icon={<SyncOutlined spin={refreshingApp} />}
              onClick={handleRefreshApp}
              loading={!isMobile && refreshingApp}
              disabled={refreshingApp}
              title={t('account.refreshApp')}
              aria-label={t('account.refreshApp')}
              style={
                isMobile
                  ? {
                      width: 36,
                      height: 36,
                      minWidth: 36,
                      padding: 0,
                      background: COLORS.accent,
                      borderColor: COLORS.accent,
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  : {
                      color: COLORS.accent,
                      borderColor: COLORS.accent,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: 600,
                    }
              }
            >
              {!isMobile && t('account.refreshApp')}
            </Button>

            {/* Menu utilisateur */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button type="text" style={{ padding: isMobile ? '0 4px' : '0 8px' }}>
                <Space size={isMobile ? 4 : 8}>
                  <Avatar 
                    size="small" 
                    icon={<UserOutlined />}
                    style={{ 
                      background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentHover} 100%)`,
                      boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                    }}
                  />
                  {!isMobile && <Text strong>{user?.email}</Text>}
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Header>

        {/* Contenu principal */}
        <Content
          style={{
            margin: isMobile ? '16px' : '24px',
            padding: isMobile ? '16px' : '24px',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            minHeight: 'calc(100vh - 112px)',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
    </>
  );
};

export default SuperAdminLayout;
